import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPaperclip, faPaperPlane, faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	StaffTicket,
	TICKET_ATTACHMENT_ALLOWED_MIME_TYPES,
	TICKET_ATTACHMENT_MAX_FILES,
	TICKET_MESSAGE_MAX_LENGTH,
	TicketStatus,
} from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";
import {
	AttachmentRejection,
	formatFileSize,
	selectTicketAttachments,
	TICKET_ATTACHMENT_MAX_MB,
} from "@/components/SupportTicket/ticketAttachments";
import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

import { ReplyOutcome } from "@/pages/Private/pages/Support/pages/TicketDetail/hooks/useTicketReply";
import { ticketUserFirstName } from "@/pages/Private/pages/Support/pages/TicketDetail/utils/ticketThread";

import { useStaffTicketReply } from "../hooks/useStaffTicketReply";
import {
	buildStaffCommentRequest,
	ComposerMode,
	composerLabels,
	defaultThenSetStatus,
	THEN_SET_STATUSES,
} from "../utils/staffComposer";

interface StaffTicketComposerProps {
	ticket: Pick<StaffTicket, "id" | "status" | "createdBy">;
	onSent: (outcome: ReplyOutcome, mode: ComposerMode) => void;
}

/**
 * The staff reply box (mockup `.composer`): text, the attach picker, the "Internal note" toggle
 * that recolours the box and hides "Then set", the customer-notified hint, "Then set" over every
 * status (decision 8) and "Send reply" / "Add note". Posts then uploads (decision 1).
 */
export const StaffTicketComposer: FunctionComponent<StaffTicketComposerProps> = ({
	ticket,
	onSent,
}) => {
	const { t } = useTranslation();
	const { send, isSending } = useStaffTicketReply(ticket.id);
	const [mode, setMode] = useState<ComposerMode>("PUBLIC");
	const [message, setMessage] = useState("");
	const [files, setFiles] = useState<File[]>([]);
	const [rejection, setRejection] = useState<AttachmentRejection | null>(null);
	// null = follow the ticket's status (which the refetch after a send updates); a pick sticks
	// until the next send. No effect needed to resync.
	const [thenSetOverride, setThenSetOverride] = useState<TicketStatus | null>(null);

	const isInternal = mode === "INTERNAL";
	const labels = composerLabels(mode);
	const customerName = ticketUserFirstName(ticket.createdBy);
	const thenSet = thenSetOverride ?? defaultThenSetStatus(ticket.status);

	// Memoised: the shared Dropdown re-selects from an effect whenever `data` changes identity.
	const statusOptions: AutoCompleteItem[] = useMemo(
		() =>
			THEN_SET_STATUSES.map((status) => ({
				id: status,
				title: t(`support.staff.status.${status}`),
			})),
		[t]
	);
	const selectedStatus = statusOptions.find((option) => option.id === thenSet) ?? statusOptions[0];

	const handleThenSet = (item: AutoCompleteItem) => {
		const next = item.id as TicketStatus;

		// A no-op on the same value: the Dropdown's sync effect calls this on every render.
		if (next !== thenSet) {
			setThenSetOverride(next);
		}
	};

	const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
		const selection = selectTicketAttachments(files, Array.from(event.target.files ?? []));

		setFiles(selection.files);
		setRejection(selection.rejection);
		// Let the same file be picked again after it was removed.
		event.target.value = "";
	};

	const handleRemove = (name: string) => {
		setFiles(files.filter((candidate) => candidate.name !== name));
		setRejection(null);
	};

	const handleSend = async () => {
		const outcome = await send(buildStaffCommentRequest(mode, message.trim(), thenSet), files);

		if (outcome.ok) {
			setMessage("");
			setFiles([]);
			setRejection(null);
			setThenSetOverride(null);
		}

		onSent(outcome, mode);
	};

	return (
		<div
			className={classNames(
				"rounded-xl border transition-colors duration-200",
				isInternal
					? "border-status-warning-light bg-status-running-light"
					: "border-gray-200 bg-white"
			)}
			data-testid="staff-composer"
		>
			<textarea
				className="block w-full resize-none rounded-t-xl bg-transparent px-3.5 py-3 text-sm text-text outline-none placeholder:text-text-meta"
				maxLength={TICKET_MESSAGE_MAX_LENGTH}
				placeholder={t(`support.staff.detail.composer.${labels.placeholder}`, {
					name: customerName,
				})}
				rows={3}
				value={message}
				onChange={(event) => setMessage(event.target.value)}
			/>
			{files.length > 0 && (
				<div className="flex flex-wrap gap-2 px-3.5 pb-2">
					{files.map((file) => (
						<span
							key={file.name}
							className="inline-flex h-8 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-text"
						>
							{file.name}
							<span className="text-text-meta">{formatFileSize(file.size)}</span>
							<button
								aria-label={t("support.modal.remove", { name: file.name })}
								className="cursor-pointer text-text-meta hover:text-text"
								type="button"
								onClick={() => handleRemove(file.name)}
							>
								<Icon icon={faXmark} />
							</button>
						</span>
					))}
				</div>
			)}
			{rejection && (
				<p className="px-3.5 pb-2 text-xs text-status-error">
					{t(`support.modal.errors.${rejection.key}`, rejection.params)}
				</p>
			)}
			<div
				className={classNames(
					"flex flex-wrap items-center gap-3 border-t px-3 py-2",
					isInternal ? "border-status-warning-light" : "border-gray-200"
				)}
			>
				<label
					className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-text-meta transition-colors duration-200 hover:bg-status-info-light hover:text-text"
					title={t("support.detail.composer.hint", {
						maxFiles: TICKET_ATTACHMENT_MAX_FILES,
						maxMb: TICKET_ATTACHMENT_MAX_MB,
					})}
				>
					<Icon icon={faPaperclip} />
					{t("support.detail.composer.attach")}
					<input
						accept={TICKET_ATTACHMENT_ALLOWED_MIME_TYPES.join(",")}
						aria-label={t("support.detail.composer.attach")}
						className="hidden"
						multiple
						type="file"
						onChange={handlePick}
					/>
				</label>
				<label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-semibold text-text">
					<input
						aria-label={t("support.staff.detail.composer.internalToggle")}
						checked={isInternal}
						className="sr-only"
						type="checkbox"
						onChange={() => setMode(isInternal ? "PUBLIC" : "INTERNAL")}
					/>
					<Toggle isChecked={isInternal} />
					{t("support.staff.detail.composer.internalToggle")}
				</label>
				<span
					className={classNames(
						"text-xs",
						isInternal ? "text-status-wrong-person" : "text-text-meta"
					)}
					data-testid="composer-hint"
				>
					{t(`support.staff.detail.composer.${labels.hint}`, { name: customerName })}
				</span>
				<div className="flex-1" />
				{labels.showThenSet && (
					<div className="flex-shrink-0">
						<Dropdown
							classNameDropdown="w-max min-w-[200px]"
							data={statusOptions}
							defaultValue={selectedStatus}
							floating={true}
							handleSelect={handleThenSet}
							size={ButtonSize.XS}
							// The Dropdown translates its title itself and appends ": <selection>".
							title="support.staff.detail.composer.thenSet"
							value={selectedStatus}
						/>
					</div>
				)}
				<Button
					disabled={message.trim().length === 0 || isSending}
					image={isInternal ? undefined : <Icon className="mr-1.5" icon={faPaperPlane} />}
					isLoading={isSending}
					size={ButtonSize.XS}
					title={t(`support.staff.detail.composer.${labels.send}`)}
					type="button"
					onClick={() => void handleSend()}
				/>
			</div>
		</div>
	);
};
